/**
 * Eastern wall clock ⇄ UTC instant, for times staff type in Eastern regardless of
 * where they sit (ticket sales campaigns start and end on the company's clock).
 *
 * A wall clock here is the `YYYY-MM-DDTHH:mm` string a `datetime-local` input
 * reads and writes. It is never parsed with `new Date(value)`, which would read it
 * in the browser's or server's own zone.
 */

export const EASTERN_TIME_ZONE = 'America/New_York';

const WALL_CLOCK_RE = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/;
const HOUR_MS = 3_600_000;

const pad = (n: number) => String(n).padStart(2, '0');

// Built once: a formatter is costly to create and these run per row on lists.
const EASTERN_PARTS = new Intl.DateTimeFormat('en-US', {
  timeZone: EASTERN_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  // h23 rather than hour12:false, which some engines render as 24:00 at midnight.
  hourCycle: 'h23',
});

function wallClockOf(instantMs: number): string {
  const parts = EASTERN_PARTS.formatToParts(instantMs);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '00';
  return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}`;
}

/** The Eastern wall clock an instant shows, as `YYYY-MM-DDTHH:mm`. Throws a
 *  RangeError for an invalid date. */
export function instantToEasternWallClock(instant: Date | string): string {
  return wallClockOf(new Date(instant).getTime());
}

/** The Eastern calendar date an instant falls on, as `YYYY-MM-DD`. */
export function easternDateOf(instant: Date | string = new Date()): string {
  return instantToEasternWallClock(instant).slice(0, 10);
}

/**
 * The instant an Eastern wall clock (`YYYY-MM-DDTHH:mm`) names, or null when the
 * string is not a real calendar date and time.
 *
 * Eastern is UTC-4 or UTC-5, so the instant is one of those two readings; the one
 * that formats back to the same wall clock wins. On the autumn change, when 1:30
 * happens twice, the earlier one is taken. On the spring change, when 2:30 never
 * happens, the time is moved an hour later, past the gap.
 */
export function easternWallClockToInstant(wallClock: string): Date | null {
  const m = WALL_CLOCK_RE.exec(wallClock.trim());
  if (!m) return null;
  const [year, month, day, hour, minute] = m.slice(1).map(Number);
  const asUtc = Date.UTC(year, month - 1, day, hour, minute);
  const asUtcDate = new Date(asUtc);
  if (
    asUtcDate.getUTCDate() !== day ||
    asUtcDate.getUTCMonth() !== month - 1 ||
    asUtcDate.getUTCHours() !== hour ||
    minute > 59
  ) {
    return null;
  }

  const target = `${year}-${pad(month)}-${pad(day)}T${pad(hour)}:${pad(minute)}`;
  const candidates = [4, 5].map((h) => asUtc + h * HOUR_MS);
  const exact = candidates.filter((ms) => wallClockOf(ms) === target);
  if (exact.length > 0) return new Date(Math.min(...exact));
  // Inside the spring-forward gap: the standard-time reading lands an hour later.
  return new Date(asUtc + 5 * HOUR_MS);
}
